// ──────────────────────────────────────────────────────────────────────────
// Trust Portal — Help Text Data Source
// ──────────────────────────────────────────────────────────────────────────
//
// Single source of truth for the help panel content shown on the 10 Trust
// Portal screens. Each entry is keyed by screenId and follows the shared
// HelpEntry shape (whatItDoes / howToNavigate / whatItContains /
// keyActions + optional relatedLinks), which means the HelpPanel
// component renders every domain's help the same way.
//
// Content below is a reasonable placeholder derived from the screen names
// and the Pacific Life Sub-Provider Trust and Compliance Reference deck
// context. Swap each entry's prose for Ryan's approved handoff text
// (jarvis-trust-portal-help-text.docx) when it lands — the HelpPanel
// component reads directly from this file, so editing values here is all
// that's needed to update what users see.

import type { HelpEntry } from './helpTextTypes';

export type TrustPortalScreenId =
  | 'trust-portal-overview'
  | 'purpose-and-context'
  | 'sub-provider-scope'
  | 'tsg-security-posture'
  | 'trust-portal-quick-reference'
  | 'requesting-full-audit-reports'
  | 'vendor-profile-railway'
  | 'vendor-profile-anthropic'
  | 'vendor-profile-openai'
  | 'vendor-profile-mistral';

// Trust Portal entries carry a screenId for self-reference in addition to
// the shared HelpEntry fields. HelpPanel only cares about the HelpEntry
// shape, so it renders Trust Portal entries the same way as any other
// domain's entries.
export interface TrustPortalHelpEntry extends HelpEntry {
  screenId: TrustPortalScreenId;
}

export const TRUST_PORTAL_HELP_TEXT: Record<TrustPortalScreenId, TrustPortalHelpEntry> = {
  'trust-portal-overview': {
    screenId: 'trust-portal-overview',
    title: 'Trust Portal Overview',
    whatItDoes:
      'This is the landing page for the Jarvis Trust Portal — a single-pane-of-glass view of TSG\'s security posture and the sub-provider chain that powers the platform. It exists so client security reviewers, third-party risk managers, and new TSG consultants can quickly orient themselves without having to piece together documents from multiple sources.',
    howToNavigate:
      'Use the left-side navigation to jump between the six informational sections (Purpose, Scope, Security Posture, Quick Reference, Audit Report Pathways, and Vendor Profiles). Each section is self-contained and can be read in any order, though first-time visitors are encouraged to read Purpose and Scope first for grounding.',
    whatItContains:
      'A short welcome, the current TSG overall compliance snapshot, and entry-point tiles for each of the other nine screens. No sensitive information is surfaced here — this is intentionally a high-level map.',
    keyActions:
      'Click any section tile to dive into its detail view. The help icon (shown in the top-right of every Trust Portal screen) can be re-opened at any time if you need a refresher on what a particular screen is for.',
  },

  'purpose-and-context': {
    screenId: 'purpose-and-context',
    title: 'Purpose and Context',
    whatItDoes:
      'Explains why the Trust Portal exists and who it is intended for. This screen sets the editorial scope of the entire portal — what you will and will not find here — so that reviewers can calibrate their expectations before reading the detailed sections.',
    howToNavigate:
      'This is an informational, read-top-to-bottom screen. Three subsections cover Intent, Audience, and Scope Boundaries. There are no interactive elements.',
    whatItContains:
      'A written statement of purpose, a description of the three primary audiences (client security reviewers, third-party risk managers, new TSG consultants), and an explicit list of topics that are deliberately out of scope for the Trust Portal (such as client-specific RCSA findings or engagement-level deliverables).',
    keyActions:
      'None — this is a read-only context screen. Use the left navigation to continue to Sub-Provider Scope or Security Posture.',
  },

  'sub-provider-scope': {
    screenId: 'sub-provider-scope',
    title: 'Sub-Provider Scope',
    whatItDoes:
      'Defines which upstream sub-providers are in-scope for TSG\'s trust disclosure and why. This is the authoritative reference for anyone asking "does TSG depend on vendor X, and if so, how?"',
    howToNavigate:
      'Scroll the sub-provider list; each row shows the vendor name, tier classification, and a one-line justification. Tiered sub-providers (Tier 1, Tier 2, Tier 3) are color-coded for quick visual scanning.',
    whatItContains:
      'A tiered list of sub-providers TSG relies on, their roles in the Jarvis platform (hosting, model inference, analytics, etc.), and a note on any upstream vendors that are intentionally excluded from scope along with the reason for exclusion.',
    keyActions:
      'Click a sub-provider name to jump straight to its Vendor Profile screen (Railway, Anthropic, OpenAI, or Mistral). If a sub-provider is not yet onboarded into the Vendor Management module, the link will open a placeholder screen instead.',
  },

  'tsg-security-posture': {
    screenId: 'tsg-security-posture',
    title: 'TSG Security Posture (SOC 2 roadmap + controls)',
    whatItDoes:
      'Shows the current state of TSG\'s own security posture, including the SOC 2 Type II roadmap, the control families that are currently covered, and pointers to the evidence library. This is the screen client reviewers typically spend the most time on.',
    howToNavigate:
      'Four collapsible sections — Roadmap, Controls, Evidence, and Attestations. Click any section header to expand it. The Roadmap section is expanded by default so the current milestone is visible without interaction.',
    whatItContains:
      'A visual timeline of SOC 2 Type II milestones with current status, a matrix of covered control families (Access, Change Management, Incident Response, etc.), a pointer to the evidence library, and the current attestation status with the most recent auditor report date.',
    keyActions:
      'Request the current SOC 2 report (routes you to the Requesting Full Audit Reports screen). Download a summary PDF of control coverage. View linked evidence items in the evidence library.',
  },

  'trust-portal-quick-reference': {
    screenId: 'trust-portal-quick-reference',
    title: 'Trust Portal Quick Reference',
    whatItDoes:
      'Provides a flat list of verification URLs so reviewers can independently confirm TSG\'s and each sub-provider\'s public compliance claims without relying on TSG-hosted documents. This is the "trust but verify" screen.',
    howToNavigate:
      'Scroll the list; each row is a vendor + a verification URL + the date the link was last confirmed working. Links are grouped by vendor, so Railway\'s links are together, Anthropic\'s are together, etc.',
    whatItContains:
      'A table of vendor name → verification URL → last-verified date → link type (SOC 2 registry, trust page, public attestation, etc.). Broken links are flagged in red so reviewers know not to waste time on them.',
    keyActions:
      'Click any URL to open it in a new browser tab. Use the copy-to-clipboard button next to each URL to paste it into a risk management tool. If you find a broken link, let the TSG Trust team know via the contact on the Purpose screen.',
  },

  'requesting-full-audit-reports': {
    screenId: 'requesting-full-audit-reports',
    title: 'Requesting Full Audit Reports',
    whatItDoes:
      'Explains the three pathways available for requesting full audit reports from TSG or its sub-providers, along with the SLA and contact information for each pathway. Use this screen when the summary information elsewhere in the portal is not enough for your review.',
    howToNavigate:
      'Three side-by-side pathway cards — Self-Serve (public reports), NDA Required (controlled distribution), and White-Glove (direct introductions). Click any card to see the full details for that pathway.',
    whatItContains:
      'For each pathway: a plain-language description, the typical response SLA, the required prerequisites (e.g. NDA on file), the contact email or form, and any downloadable templates (NDA, request form).',
    keyActions:
      'Contact the Trust team via the listed email for your chosen pathway. Download the NDA template if needed. Submit a formal report request through the form linked in the White-Glove card.',
  },

  'vendor-profile-railway': {
    screenId: 'vendor-profile-railway',
    title: 'Vendor Profile — Railway',
    whatItDoes:
      'Shows the live Jarvis Vendor Management card for Railway, TSG\'s production hosting provider. This is the same vendor detail view used by the TSG vendor risk team, so the information you see here is current — not a snapshot.',
    howToNavigate:
      'The vendor detail pane is organized into five tabs: Overview, Documents, Reviews, Contacts, and Onboarding. Switch between tabs using the tab bar under the vendor name. Close the detail view via the X in the top-right corner.',
    whatItContains:
      'Railway\'s tier classification, risk level, SOC 2 status (Type II, August 2025), uploaded compliance documents, review history, key contacts, and the onboarding checklist status. The risk color indicator in the top-left reflects the current aggregated risk score.',
    keyActions:
      'Download any uploaded compliance document from the Documents tab. Review the vendor assessment history in the Reviews tab. Edit vendor metadata (TSG users only) via the Edit button in the header.',
  },

  'vendor-profile-anthropic': {
    screenId: 'vendor-profile-anthropic',
    title: 'Vendor Profile — Anthropic',
    whatItDoes:
      'Shows the live Jarvis Vendor Management card for Anthropic, TSG\'s primary large language model provider. All AI responses generated by Jarvis ultimately flow through Anthropic\'s Claude API, which makes this one of the highest-impact sub-providers.',
    howToNavigate:
      'Same five-tab layout as every other vendor profile — Overview, Documents, Reviews, Contacts, Onboarding. Navigate tabs via the tab bar; close via the X.',
    whatItContains:
      'Anthropic\'s tier classification (Tier 1 — AI provider), current SOC 2 posture, data handling and retention commitments, uploaded attestations and policies, and any recent vendor reviews. Includes a note on the Anthropic API usage scope within Jarvis.',
    keyActions:
      'Download Anthropic\'s most recent public attestation. View the data handling summary in the Overview tab. Check the Reviews tab for the last TSG-side vendor assessment date.',
  },

  'vendor-profile-openai': {
    screenId: 'vendor-profile-openai',
    title: 'Vendor Profile — OpenAI',
    whatItDoes:
      'Shows the live Jarvis Vendor Management card for OpenAI, used as a secondary model provider for specific workloads within Jarvis. Use this screen to understand when and how OpenAI is invoked, and what compliance commitments are in place.',
    howToNavigate:
      'Same five-tab layout as the other vendor profiles — Overview, Documents, Reviews, Contacts, Onboarding.',
    whatItContains:
      'OpenAI\'s tier classification, current SOC 2 / ISO posture, data handling commitments relevant to API usage, uploaded attestations, and the list of Jarvis features or workloads that route through OpenAI versus Anthropic.',
    keyActions:
      'Download the current attestation. Review the scope-of-use note to see which Jarvis features invoke OpenAI. Check the most recent TSG vendor review date in the Reviews tab.',
  },

  'vendor-profile-mistral': {
    screenId: 'vendor-profile-mistral',
    title: 'Vendor Profile — Mistral (Placeholder)',
    whatItDoes:
      'This is a placeholder for the Mistral vendor profile. Mistral is currently available in Jarvis as an optional AI provider configuration but has not yet been onboarded as a formal vendor in the Vendor Management module. Once onboarding is complete, this screen will switch over to the live Vendor Management card format used by the other vendor profiles.',
    howToNavigate:
      'No navigation is available on this placeholder screen. Once Mistral is onboarded, this screen will gain the same five-tab layout (Overview, Documents, Reviews, Contacts, Onboarding) as the Railway, Anthropic, and OpenAI profiles.',
    whatItContains:
      'A short status note explaining that Mistral onboarding is pending, a pointer to where Mistral currently surfaces in Jarvis (the AI provider configuration in Admin → Security), and an estimated date for when the live vendor profile will replace this placeholder.',
    keyActions:
      'None — this is a read-only placeholder. To check on Mistral onboarding status, contact the TSG Trust team via the email listed on the Purpose screen.',
  },
};

// Convenience accessor. Callers should always use this helper rather than
// indexing TRUST_PORTAL_HELP_TEXT directly — that way, if an unknown
// screenId is ever passed in (e.g. from a typo in a new component), we
// fail soft with a clear fallback message instead of crashing.
export function getTrustPortalHelpEntry(screenId: TrustPortalScreenId): TrustPortalHelpEntry {
  const entry = TRUST_PORTAL_HELP_TEXT[screenId];
  if (entry) return entry;
  return {
    screenId,
    title: 'Help content not available',
    whatItDoes: 'No help text has been configured for this screen yet.',
    howToNavigate: '',
    whatItContains: '',
    keyActions: '',
  };
}
